import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser } from '../auth';
import axios from 'axios';
import config from '../config';

function Login() {
  const [activePortal, setActivePortal] = useState('student');
  const [fading, setFading] = useState(false);
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [cognitoUserObj, setCognitoUserObj] = useState(null);
  const navigate = useNavigate();

  const portals = {
    student: {
      title: 'Student Portal',
      subtitle: 'Access your academic records',
      icon: '👨‍🎓',
      color: 'blue',
      placeholder: 'Enter Student ID (e.g. stu001)',
      prefix: 'stu',
      gradient: 'from-slate-900 via-blue-950 to-slate-900'
    }, 
    employee: {
      title: 'Employee Portal',
      subtitle: 'Access your HR and payroll records',
      icon: '👔',
      color: 'green',
      placeholder: 'Enter Employee ID (e.g. emp001)',
      prefix: 'emp',
      gradient: 'from-slate-900 via-green-950 to-slate-900'
    },
    admin: {
      title: 'Admin Portal',
      subtitle: 'Manage all institutional records',
      icon: '🔐',
      color: 'purple',
      placeholder: 'Enter Admin ID (e.g. adm001)',
      prefix: 'adm',
      gradient: 'from-slate-900 via-purple-950 to-slate-900'
    }
  };

  const portal = portals[activePortal];

  const buttonColors = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-green-600 hover:bg-green-700',
    purple: 'bg-purple-600 hover:bg-purple-700'
  };

  const handlePortalSwitch = (newPortal) => {
    if (newPortal === activePortal) return;
    setFading(true);
    setUserId('');
    setPassword('');
    setError('');
    setNewPasswordRequired(false);
    setTimeout(() => {
      setActivePortal(newPortal);
      setTimeout(() => {
        setFading(false);
      }, 50);
    }, 650);
  };

  const handleLogin = async () => {
    if (!userId || !password) {
      setError('Please enter all fields');
      return;
    }
    setLoading(true);
    setError('');

    const finalUserId = userId.toLowerCase().trim();

    try {
      const result = await loginUser(finalUserId, password);

      if (result.newPasswordRequired) {
        setNewPasswordRequired(true);
        setCognitoUserObj(result.cognitoUser);
        setLoading(false);
        return;
      }

      const userPrefix = finalUserId.substring(0, 3);
      const correctRole =
        userPrefix === 'stu' ? 'student' :
        userPrefix === 'adm' ? 'admin' :
        userPrefix === 'emp' ? 'employee' : null;

      if (correctRole !== activePortal) {
        setError('Invalid username or password');
        setLoading(false);
        return;
      }

      sessionStorage.setItem('userId', finalUserId);
      sessionStorage.setItem('role', activePortal);
      sessionStorage.setItem('authenticated', 'true');

      try {
        await axios.post(`${config.API_BASE}/auditlogs`, {
          userId: finalUserId,
          action: 'LOGIN',
          targetId: finalUserId,
          ipAddress: 'client',
          result: 'SUCCESS'
        });
      } catch(e) { console.error('Audit log failed:', e); }

      if (activePortal === 'student') navigate('/student');
      else if (activePortal === 'admin') navigate('/admin');
      else if (activePortal === 'employee') navigate('/employee');

    } catch (err) {
      setError('Invalid username or password');
    }
    setLoading(false);
  };

  const handleNewPassword = () => {
    const finalUserId = userId.toLowerCase().trim();
    cognitoUserObj.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: async () => {
        sessionStorage.setItem('userId', finalUserId);
        sessionStorage.setItem('role', activePortal);
        sessionStorage.setItem('authenticated', 'true');

        try {
          await axios.post(`${config.API_BASE}/auditlogs`, {
            userId: finalUserId,
            action: 'LOGIN',
            targetId: finalUserId,
            ipAddress: 'client',
            result: 'SUCCESS'
          });
        } catch(e) { console.error('Audit log failed:', e); }

        if (activePortal === 'student') navigate('/student');
        else if (activePortal === 'admin') navigate('/admin');
        else if (activePortal === 'employee') navigate('/employee');
      },
      onFailure: (err) => {
        setError(err.message);
      }
    });
  };

  return (
    <div
      className={`min-h-screen bg-gradient-to-br ${portal.gradient} flex items-center justify-center p-4`}
      style={{ transition: 'background 0.30s ease-in-out' }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8"
        style={{
          opacity: fading ? 0 : 1,
          transform: fading ? 'scale(0.97)' : 'scale(1)',
          transition: 'opacity 0.30s ease-in-out, transform 0.30s ease-in-out'
        }}
      >
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-1 brand-logo">
            <img src="/cloudrecords-logo.svg" alt="CloudRecords" className="w-10 h-10" />
            <h1 className="text-2xl font-bold text-slate-800">CloudRecords</h1>
          </div>
          <p className="text-slate-400 text-xs mt-1">Serverless Database Management System</p>
          <div className="w-16 h-1 bg-blue-600 rounded mx-auto mt-2"></div>
        </div>

        {/* Portal Header */}
        <div className={`text-center mb-6 p-4 rounded-xl ${
          activePortal === 'student' ? 'bg-blue-50' :
          activePortal === 'employee' ? 'bg-green-50' :
          'bg-purple-50'
        }`}>
          <div className="text-3xl mb-1">{portal.icon}</div>
          <h2 className={`font-bold text-lg ${
            activePortal === 'student' ? 'text-blue-700' :
            activePortal === 'employee' ? 'text-green-700' :
            'text-purple-700'
          }`}>{portal.title}</h2>
          <p className="text-slate-500 text-xs">{portal.subtitle}</p>
        </div>

        {!newPasswordRequired ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-slate-600 mb-2">User ID</label>
              <input
                type="text"
                placeholder={portal.placeholder}
                value={userId}
                onChange={(e) => setUserId(e.target.value.toLowerCase())}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-600 mb-2">Password</label>
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg mb-4 border border-red-200">
                {error}
              </div>
            )}

            <button
              onClick={handleLogin}
              disabled={loading}
              className={`w-full ${buttonColors[portal.color]} text-white font-semibold py-3 rounded-xl transition duration-200 text-sm disabled:opacity-50`}
            >
              {loading ? 'Logging in...' : `Login to ${portal.title}`}
            </button>

            {/* Portal Switch Links */}
            <div className="mt-6 pt-4 border-t border-slate-100">
              {activePortal === 'student' && (
                <div className="flex flex-col gap-2 text-center">
                  <button
                    onClick={() => handlePortalSwitch('employee')}
                    className="text-green-600 hover:text-green-700 text-sm font-medium hover:underline transition"
                  >
                    Are you an Employee? Login here →
                  </button>
                  <button
                    onClick={() => handlePortalSwitch('admin')}
                    className="text-purple-600 hover:text-purple-700 text-sm font-medium hover:underline transition"
                  >
                    Login as Admin →
                  </button>
                </div>
              )}

              {activePortal === 'employee' && (
                <div className="flex flex-col gap-2 text-center">
                  <button
                    onClick={() => handlePortalSwitch('student')}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium hover:underline transition"
                  >
                    Are you a Student? Login here →
                  </button>
                  <button
                    onClick={() => handlePortalSwitch('admin')}
                    className="text-purple-600 hover:text-purple-700 text-sm font-medium hover:underline transition"
                  >
                    Login as Admin →
                  </button>
                </div>
              )}

              {activePortal === 'admin' && (
                <div className="flex flex-col gap-2 text-center">
                  <button
                    onClick={() => handlePortalSwitch('student')}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium hover:underline transition"
                  >
                    Are you a Student? Login here →
                  </button>
                  <button
                    onClick={() => handlePortalSwitch('employee')}
                    className="text-green-600 hover:text-green-700 text-sm font-medium hover:underline transition"
                  >
                    Are you an Employee? Login here →
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="mb-4">
              <p className="text-sm text-slate-600 mb-4 bg-blue-50 p-3 rounded-lg">
                First time login — please set a new password.
              </p>
              <label className="block text-sm font-semibold text-slate-600 mb-2">New Password</label>
              <input
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg mb-4 border border-red-200">
                {error}
              </div>
            )}

            <button
              onClick={handleNewPassword}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition duration-200 text-sm"
            >
              Set Password & Login
            </button>
          </>
        )}

        <p className="text-center text-xs text-slate-400 mt-6">
          Amity University Lucknow · SDBMS Major Project · 2026
        </p>
      </div>
    </div>
  );
}

export default Login;